import { useEffect, useRef, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { LockClosedIcon } from '@heroicons/react/24/outline'

interface InputProps {
  label?: string
  placeholder?: string
  type?: 'number' | 'text' | 'password' | 'textOnly'
  disabled?: boolean
  stackedText?: string
  textAsNumber?: boolean
  noSpecialCaracters?: boolean
  numbersAndDots?: boolean
  numberAsCurrency?: { precision: number }
  showInline?: boolean
  maxLength?: number
  autoFocus?: boolean
  value?: string
  maxValue?: number
  webBrowserAutocomplete?: boolean
  allowComma?: boolean
}

const showAsCurrency = (amount: number, precision: number) =>
  new Intl.NumberFormat('en-EN', {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: precision,
  }).format(amount)

const Input = (props: UseControllerProps & InputProps) => {
  const {
    label,
    placeholder,
    defaultValue,
    type = 'text',
    disabled,
    stackedText,
    numberAsCurrency,
    textAsNumber,
    noSpecialCaracters,
    numbersAndDots,
    showInline,
    maxLength,
    autoFocus,
    value,
    maxValue,
    webBrowserAutocomplete = true,
    allowComma = false,
  } = props
  const { field, fieldState } = useController(props)

  const initialValue =
    type === 'number' && numberAsCurrency
      ? showAsCurrency(
          field.value ?? defaultValue ?? undefined,
          numberAsCurrency.precision
        )
      : (field.value ?? defaultValue)

  const [input, setInput] = useState<string>(initialValue ?? '')

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (type === 'number') field.onChange(defaultValue ?? undefined)
    else if (defaultValue) field.onChange(defaultValue)
  }, [])

  useEffect(() => {
    if (inputRef.current && autoFocus) {
      inputRef.current.focus()
    }
  }, [])

  useEffect(() => {
    if (value) setInput(value)
  }, [value])

  return (
    <div
      className={`w-full ${showInline ? 'inline-flex items-center gap-2' : ''}`}
    >
      {label && (
        <label
          htmlFor={label}
          className={`block text-sm font-medium ${
            disabled ? 'text-gray-400' : 'text-gray-700'
          }`}
        >
          <span className="inline-flex items-center">
            {label}
            {disabled && <LockClosedIcon className="h-4 px-2" />}
          </span>
        </label>
      )}
      <div className="relative mt-1 inline-flex w-full items-center rounded-md shadow-sm">
        {stackedText && (
          <span
            className={`${
              fieldState.error
                ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-400 text-gray-500 focus:border-gray-600 focus:ring-gray-400'
            } w-full rounded-md rounded-r-none border border-r-0 p-2 text-right sm:text-sm`}
          >
            {stackedText && stackedText}
          </span>
        )}
        <input
          ref={inputRef}
          type={type === 'password' ? type : 'text'}
          autoComplete={!webBrowserAutocomplete ? 'new-password' : undefined}
          className={`${
            fieldState.error
              ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
              : 'focus:border-gray-600 focus:ring-gray-400'
          } block w-full rounded-md placeholder:text-slate-400 sm:text-sm ${
            stackedText ? 'rounded-l-none' : ''
          } ${disabled ? 'border-gray-300 text-gray-400' : 'border-gray-500 text-gray-500'}`}
          placeholder={placeholder && placeholder}
          value={
            numberAsCurrency || type === 'number'
              ? input === '$0.00'
                ? ''
                : input
              : (field.value ?? '')
          }
          maxLength={maxLength && maxLength}
          onChange={(e) => {
            const value = e.target.value

            if (type === 'textOnly') {
              // Ensure that only letters are allowed
              const onlyLetters = /^[a-zA-ZáéíóúÁÉÍÓÚüÜñÑ\s]*$/
              if (onlyLetters.test(value) || value === '') {
                setInput(value)
                field.onChange(value)
              }
            } else if (!!noSpecialCaracters) {
              // Permitir solo letras y números, sin espacios
              const validValue = value.replace(/[^a-zA-Z0-9]/g, '')
              setInput(validValue)
              field.onChange(validValue)
            } else if (textAsNumber && type === 'text') {
              if (
                (value.charCodeAt(value.length - 1) >= 48 &&
                  value.charCodeAt(value.length - 1) <= 57) ||
                value.charCodeAt(value.length - 1) === 43 ||
                value === ''
              ) {
                setInput(value)
                field.onChange(value)
              }
            } else if (numbersAndDots && type === 'text') {
              if (
                (value.charCodeAt(value.length - 1) >= 48 &&
                  value.charCodeAt(value.length - 1) <= 57) ||
                value.charCodeAt(value.length - 1) === 46 ||
                value === ''
              ) {
                setInput(value)
                field.onChange(value)
              }
            } else if (numberAsCurrency && type === 'number') {
              if (
                (value.charCodeAt(value.length - 1) >= 48 &&
                  value.charCodeAt(value.length - 1) <= 57) ||
                [36, 44, 46].includes(value.charCodeAt(value.length - 1)) ||
                value === ''
              ) {
                if (value.split('.').length > 2) return
                setInput('$' + value.replace(/[$,]/g, ''))
                field.onChange(Number(value.replace(/[$,]/g, '')))
              }
            } else if (allowComma && type === 'number') {
              const lastChar = value.charAt(value.length - 1)
              const isDigit = lastChar >= '0' && lastChar <= '9'
              const isDot = lastChar === '.'
              const isComma = lastChar === ','

              const hasDot = value.slice(0, -1).includes('.')
              const hasComma = value.slice(0, -1).includes(',')

              const isEmpty = value === ''

              if (
                isDigit ||
                isEmpty ||
                (isDot && !hasDot && !hasComma) ||
                (isComma && !hasComma && !hasDot)
              ) {
                if (isEmpty) {
                  setInput('0')
                  field.onChange(0)
                  return
                }

                let finalValue = value
                if (input === '0' && isDigit) {
                  finalValue = lastChar
                }

                setInput(finalValue)

                let numericValue = Number(finalValue.replace(',', '.'))

                if (typeof maxValue === 'number' && numericValue > maxValue) {
                  numericValue = maxValue
                }

                field.onChange(numericValue)
              }
            } else if (type === 'number') {
              if (
                (value.charCodeAt(value.length - 1) >= 48 &&
                  value.charCodeAt(value.length - 1) <= 57) ||
                (value.charCodeAt(value.length - 1) === 46 &&
                  !value.slice(undefined, value.length - 1).includes('.')) ||
                value === ''
              ) {
                let numericValue = Number(value)

                if (typeof maxValue === 'number' && numericValue > maxValue) {
                  numericValue = maxValue
                }

                setInput(numericValue.toString())
                field.onChange(numericValue)
              }
            } else {
              setInput(value)
              field.onChange(value)
            }
          }}
          disabled={disabled}
        />

        {fieldState.error && (
          <>
            <div className="pointer-events-none absolute inset-y-0 right-0 top-2 pr-3">
              <ExclamationCircleIcon
                className="h-5 w-5 text-red-500"
                aria-hidden="true"
              />
            </div>

            <p className="absolute -bottom-5 flex flex-shrink-0 text-xs text-red-600">
              {fieldState.error?.message}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default Input
